import ErrorMessage from 'components/elements/items/ErrorMessage'
import FormTitle from 'components/elements/items/FormTitle'
import TextBox from 'components/elements/items/TextBox'
import Text from 'components/elements/texts/Text'
import { CLIENT_SETTINGS } from 'constants/clientSettings'
import { CONTRACT_INFO_FORM } from 'constants/qnState'
import React, { useEffect } from 'react'
import styled from 'styled-components'
import { convertWideKanaToHalfKana } from 'utils/commonUtils'
import { nameKanaValidation, phoneNumberValidation } from 'utils/commonUtils'
import ModalInputWrap from '../items/ModalInputWrap'

/** 共通エラーメッセージ */
const ERROR_MESSAGE_NOT_SEND = '入力内容はオペレーターに送信されません。'

/** 要請者氏名の残り入力文字数の算出（スペースを除く） */
const getRemainingLengthForNameField = (partnerText: string): number =>
  29 - convertWideKanaToHalfKana(partnerText).length

/**
 * 架電モーダル 呼び出し元を含む
 */
export const ContractInfoInModal: React.FC<ContractInfoInModalProps> = (
  props
) => {
  const {
    inputTextFirstNumber,
    inputTextSecondNumber,
    inputTextLastNumber,
    inputTextLastName,
    inputTextFirstName,
    onChangeFirstNumber,
    onChangeSecondNumber,
    onChangeLastNumber,
    onChangeLastName,
    onChangeFirstName,
    onChangePhoneNumberError,
    onChangeFullNameError,
  } = props

  // エラーメッセージ管理
  const lastNameError = nameKanaValidation(inputTextLastName)
  const firstNameError = nameKanaValidation(inputTextFirstName)
  const firstNumberError = phoneNumberValidation(inputTextFirstNumber, 3)
  const secondNumberError = phoneNumberValidation(inputTextSecondNumber, 4)
  const lastNumberError = phoneNumberValidation(inputTextLastNumber, 4)

  // アラート文表示管理
  /** 要請者名のアラート文 */
  const nameErrorMessage = nameKanaValidation(
    inputTextLastName + inputTextFirstName
  )
  /** 要請者の電話番号のアラート文 */
  const phoneNumberErrorMessage = phoneNumberValidation(
    inputTextFirstNumber + inputTextSecondNumber + inputTextLastNumber,
    11
  )

  //アラート表示制御
  const [nameAlert, setNameAlert] = React.useState<{ [key: string]: boolean }>({
    firstName: firstNameError !== '',
    lastName: lastNameError !== '',
  })
  const [phoneNumAlert, setPhoneNumAlert] = React.useState<{
    [key: string]: boolean
  }>({
    firstNumber: firstNumberError !== '',
    secondNumber: secondNumberError !== '',
    lastNumber: lastNumberError !== '',
  })

  const handleSetNameAlert = (isInvalid: boolean, key: string): void => {
    setNameAlert((prev) => ({ ...prev, [key]: isInvalid }))
  }

  const handleSetPhoneNumAlert = (isInvalid: boolean, key: string): void => {
    setPhoneNumAlert((prev) => ({ ...prev, [key]: isInvalid }))
  }

  useEffect(() => {
    onChangePhoneNumberError(phoneNumberErrorMessage !== '')
    onChangeFullNameError(nameErrorMessage !== '')
  }, [nameErrorMessage, phoneNumberErrorMessage])

  const showPhoneNumAlert = Object.values(phoneNumAlert).some((val) => val)
  const showNameAlert = Object.values(nameAlert).some((val) => val)

  const handleNameTextChange = (
    event: React.ChangeEvent<HTMLTextAreaElement>,
    setState: React.Dispatch<React.SetStateAction<string>>,
    partnerText: string
  ): void => {
    const targetValue = event.target.value
    if (
      convertWideKanaToHalfKana(targetValue).length >
      getRemainingLengthForNameField(partnerText)
    ) {
      /** 入力テキストが文字数制限を超えている場合は変更通知を送らない */
      return
    }
    setState(targetValue)
  }

  return (
    <InputFormArea>
      <InputFormWrap>
        <StyledFormTitle title="ご要請者電話番号" isOption bold />
        <InputInnerWrap>
          <ModalInputWrap
            inputKey={'firstNumber'}
            isInvalid={firstNumberError !== ''}
            width="100%"
            setShowAlert={handleSetPhoneNumAlert}
          >
            <StyledInput
              placeholder={CONTRACT_INFO_FORM.phoneNumber.label[0]}
              inputText={inputTextFirstNumber}
              maxLength={3}
              onChange={(event): void =>
                onChangeFirstNumber(event.target.value)
              }
              doNotPlaceholderMove={true}
            />
          </ModalInputWrap>
          <ModalInputWrap
            inputKey={'secondNumber'}
            isInvalid={secondNumberError !== ''}
            width="100%"
            setShowAlert={handleSetPhoneNumAlert}
          >
            <StyledInput
              placeholder={CONTRACT_INFO_FORM.phoneNumber.label[1]}
              inputText={inputTextSecondNumber}
              maxLength={4}
              onChange={(event): void =>
                onChangeSecondNumber(event.target.value)
              }
              doNotPlaceholderMove={true}
            />
          </ModalInputWrap>
          <ModalInputWrap
            inputKey={'lastNumber'}
            isInvalid={lastNumberError !== ''}
            width="100%"
            setShowAlert={handleSetPhoneNumAlert}
          >
            <StyledInput
              placeholder={CONTRACT_INFO_FORM.phoneNumber.label[2]}
              inputText={inputTextLastNumber}
              maxLength={4}
              onChange={(event): void => onChangeLastNumber(event.target.value)}
              doNotPlaceholderMove={true}
            />
          </ModalInputWrap>
        </InputInnerWrap>
      </InputFormWrap>
      {phoneNumberErrorMessage && showPhoneNumAlert && (
        <ErrorMessage
          message={phoneNumberErrorMessage + ERROR_MESSAGE_NOT_SEND}
          justifyContent="left"
          marginNone={true}
        />
      )}
      <InputFormWrap>
        <StyledFormTitle title="ご要請者お名前（カナ）" isOption bold />
        <InputInnerWrap>
          <ModalInputWrap
            inputKey={'lastName'}
            isInvalid={lastNameError !== ''}
            width="100%"
            setShowAlert={handleSetNameAlert}
          >
            <StyledInputTitle bold title="氏:" />
            <StyledNameInput
              maxLength={getRemainingLengthForNameField(inputTextFirstName)}
              placeholder={CLIENT_SETTINGS.label.contractInfo.lastName}
              inputText={inputTextLastName}
              onChange={(event): void =>
                handleNameTextChange(
                  event,
                  onChangeLastName,
                  inputTextFirstName
                )
              }
              doNotPlaceholderMove={true}
            />
          </ModalInputWrap>
          <ModalInputWrap
            inputKey={'firstName'}
            isInvalid={firstNameError !== ''}
            width="100%"
            setShowAlert={handleSetNameAlert}
          >
            <StyledInputTitle bold title="名:" />
            <StyledNameInput
              maxLength={getRemainingLengthForNameField(inputTextLastName)}
              placeholder={CLIENT_SETTINGS.label.contractInfo.firstName}
              inputText={inputTextFirstName}
              onChange={(event): void =>
                handleNameTextChange(
                  event,
                  onChangeFirstName,
                  inputTextLastName
                )
              }
              doNotPlaceholderMove={true}
            />
          </ModalInputWrap>
        </InputInnerWrap>
      </InputFormWrap>
      {nameErrorMessage && showNameAlert && (
        <ErrorMessage
          message={nameErrorMessage + ERROR_MESSAGE_NOT_SEND}
          justifyContent="left"
          marginNone={true}
        />
      )}
      <HelpInfoWrap>
        <HelpInfo>
          {'※'}
          <Span>{'電話番号とお名前を入力いただくことで'}</Span>
          {'、内容がオペレーターに送信されます。'}
        </HelpInfo>
        <HelpInfo>
          {
            '※お電話がつながらない場合、後ほどオペレーターからご連絡いたします。ただし、お時間をいただくことがあります。なお、電話番号が入力されていない場合は折り返しのご連絡はできません。'
          }
        </HelpInfo>
        <HelpInfo>
          {'※'}
          <Span>{'電話番号とお名前のどちらも入力されていない場合'}</Span>
          {'、内容は送信されません。'}
        </HelpInfo>
      </HelpInfoWrap>
    </InputFormArea>
  )
}

type ContractInfoInModalProps = {
  inputTextFirstName: string
  inputTextLastName: string
  inputTextFirstNumber: string
  inputTextSecondNumber: string
  inputTextLastNumber: string
  onChangeFirstName: React.Dispatch<React.SetStateAction<string>>
  onChangeLastName: React.Dispatch<React.SetStateAction<string>>
  onChangeFirstNumber: React.Dispatch<React.SetStateAction<string>>
  onChangeSecondNumber: React.Dispatch<React.SetStateAction<string>>
  onChangeLastNumber: React.Dispatch<React.SetStateAction<string>>
  onChangePhoneNumberError: (value: React.SetStateAction<boolean>) => void
  onChangeFullNameError: (value: React.SetStateAction<boolean>) => void
}

const StyledFormTitle = styled(FormTitle)`
  margin: 0;
  padding: 3px;
  border-radius: 1px;
  background-color: var(--color-background);
  display: flex;
  align-items: center;
`

const StyledInputTitle = styled(StyledFormTitle)`
  background-color: transparent;
`

const InputFormArea = styled.form`
  display: flex;
  flex-direction: column;
  gap: calc(10vw / 3.75);
`

const InputFormWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: calc(10vw / 3.75);
`

const InputInnerWrap = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const StyledInput = styled(TextBox)`
  width: 100%;
  margin: 0;
  padding: 10px;
  border-radius: 1px;
  border: none;
  transition: all 0.2s ease;
  font-size: 16px;
  text-align: center;
  &:focus {
    outline: none;
    border-bottom: none;
  }
  &::placeholder {
    color: #aaa;
  }
`
const StyledNameInput = styled(StyledInput)`
  text-align: left;
  background-color: transparent;
`
const HelpInfoWrap = styled.div`
  padding: 5px;
  background-color: var(--color-background);
`
const Span = styled(Text)`
  display: initial;
  font-size: 13px;
  border-bottom: 1px solid var(--color-error);
  color: var(--color-error);
`
const HelpInfo = styled(Text)`
  font-size: 13px;
  color: var(--color-text);
  margin: 0;
  &&:nth-of-type(2),
  :nth-of-type(3) {
    margin-top: 8px;
  }
`
